// Shared chart-registry types.
//
// `ValueType` is a coarser bucket than `feature.config.type` — it groups
// types that share a chart catalog. e.g. `rating` and `labeled_rating` both
// store on a 1..N scale and use the same chart options, so they map to the
// single `rating` bucket.

export type ValueType =
  | 'decimal'      // freeform numeric (decimal_value, no fixed scale)
  | 'rating'       // 1..N scale (rating, labeled_rating)
  | 'freeform'     // freeform string
  | 'enum'         // single value from a fixed set
  | 'bool'         // scalar yes/no (list<bool> stays 'list'; a bool map half stays in its map bucket)
  | 'reference'    // collection-item reference
  | 'list'         // multi-value (list of any subtype)
  | 'map'                  // (key, value) pairs with non-numeric value (count-only charts)
  | 'map_numeric'          // (key, value) pairs with numeric value, non-numeric key (count + sum charts)
  | 'map_ordered_numeric'  // (key, value) pairs with numeric key AND numeric value (key-line + per-key time series)
  | 'other';               // location, unrecognized

export interface FeatureConfigLike {
  type?: string;
  params?: {
    type?: string;
    max?: number;
    labels?: string[];
    // Yes/no (bool) configs: the two answers' labels.
    true_label?: string;
    false_label?: string;
    values?: (string | number)[];
    collection_id?: number;
    // For map sub-configs only: the unit string for the key half or
    // value half (e.g. "minutes", "rounds"). Lives in `params` rather
    // than as a sibling of `type` to match how other type-specific
    // metadata (max, labels, values, collection_id) is stored.
    // Top-level (non-map) features still use `feature.unit` instead.
    unit?: string;
    // Map sub-configs nest a `key` and a `value`, each shaped like a
    // non-map feature's config (a `type` plus its own `params`).
    key?: { type?: string; params?: FeatureConfigLike['params'] };
    value?: { type?: string; params?: FeatureConfigLike['params'] };
  };
}

const NUMERIC_MAP_VALUE_TYPES = new Set(['num', 'int', 'decimal', 'rating', 'labeled_rating']);
// Keys are stored in `key_decimal_value` only for `num` / `int` — `rating`
// and `labeled_rating` aren't valid as map keys per Stage 0's allowlist.
const ORDERABLE_MAP_KEY_TYPES = new Set(['num', 'int']);

export function getValueType(config?: FeatureConfigLike | null): ValueType {
  const t = config?.type;
  if (!t) return 'other';
  switch (t) {
    case 'decimal':
    case 'num':
    case 'int':
      return 'decimal';
    case 'rating':
    case 'labeled_rating':
      return 'rating';
    case 'freeform':
    case 'string':
      return 'freeform';
    case 'enum':
      return 'enum';
    case 'bool':
      return 'bool';
    case 'reference':
      return 'reference';
    case 'list':
      return 'list';
    case 'map': {
      const keySubType = config?.params?.key?.type;
      const valueSubType = config?.params?.value?.type;
      const valueNumeric = !!valueSubType && NUMERIC_MAP_VALUE_TYPES.has(valueSubType);
      const keyOrderable = !!keySubType && ORDERABLE_MAP_KEY_TYPES.has(keySubType);
      if (valueNumeric && keyOrderable) return 'map_ordered_numeric';
      if (valueNumeric) return 'map_numeric';
      return 'map';
    }
    default:
      return 'other';
  }
}

export type ChartId =
  | 'line'
  | 'stats'
  | 'activity'
  | 'pie'
  | 'enum_bar'
  | 'histogram'
  | 'label_cloud'
  | 'top_words'
  | 'map_sum_pie'
  | 'map_count_pie'
  | 'map_count_bar'
  | 'map_lines'
  | 'map_key_bar'
  | 'bool_calendar'
  | 'bool_weeks';

// Body components are stateless visualizations dropped inside the
// container's chrome. They own their own data fetch and internal state
// but never render a card border, title, header buttons, or fullscreen
// modal — that's the container's job. Bodies that want time-range
// paging render their own ChartTimeRangePicker; ActivityCalendarBody
// keeps its bespoke 3-month nav.
export interface ChartBodyProps {
  featureId: number;
  featureName?: string;
  unit?: string;
  config?: FeatureConfigLike | null;
  // Bodies pick a bigger layout / extra interactions when fullscreen.
  // Examples: LineChartBody adds a brush + wheel zoom in fullscreen.
  fullscreen: boolean;
  // Body-specific toggles driven from the chart-card kebab menu in the
  // chrome. Bodies that don't expose them ignore this. LineChartBody
  // reads `verticalBrushVisible` to show its Y-axis range slider in
  // fullscreen mode.
  verticalBrushVisible?: boolean;
}
